import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  getEtlFlows, createEtlFlow, updateEtlFlow, deleteEtlFlow,
  getEtlRunHistory, runEtlFlowSSE,
} from '../services/etlService';
import { getTenants } from '../services/adminService';

const NODE_W = 210;
const NODE_H = 92;

const NODE_TYPES = {
  fileUpload: { label: 'Dosya Yükleme', color: '#0ea5e9', icon: '📄' },
  sqlExecute: { label: 'SQL Adımı',     color: '#7c3aed', icon: '⚙️' },
};

const STATUS_STYLE = {
  idle:    { ring: '#cbd5e1', badge: '#94a3b8', text: 'Bekliyor' },
  running: { ring: '#f59e0b', badge: '#f59e0b', text: 'Çalışıyor' },
  success: { ring: '#16a34a', badge: '#16a34a', text: 'Tamamlandı' },
  failed:  { ring: '#dc2626', badge: '#dc2626', text: 'Hata' },
};

const uid        = () => Math.random().toString(36).slice(2, 9);
const emptyFlow  = (tenantId) => ({ id: null, tenantId, name: '', nodes: [], edges: [] });

const pulseKeyframes = `
  @keyframes etlPulse {
    0%   { box-shadow: 0 0 0 0 rgba(245,158,11,.5); }
    70%  { box-shadow: 0 0 0 10px rgba(245,158,11,0); }
    100% { box-shadow: 0 0 0 0 rgba(245,158,11,0); }
  }
`;

// ─── Wildcard dosya eşleştirme ───────────────────────────────────────────────
function matchesPattern(fileName, pattern) {
  if (!pattern || !fileName) return false;
  if (fileName.toLowerCase() === pattern.toLowerCase()) return true;
  if (!pattern.includes('*') && !pattern.includes('?')) return false;
  const regex = new RegExp(
    '^' + pattern.replace(/[.+^${}()|[\]\\]/g, '\\$&').replace(/\*/g, '.*').replace(/\?/g, '.') + '$',
    'i'
  );
  return regex.test(fileName);
}

function resolveFile(files, pattern) {
  if (!pattern || !files?.length) return undefined;
  return files.find((f) => matchesPattern(f.name, pattern));
}

// ─── Ana bileşen ─────────────────────────────────────────────────────────────
const EtlPage = () => {
  const { user, isSuperAdmin } = useSelector((s) => s.auth);

  const [mode, setMode]   = useState(isSuperAdmin ? 'design' : 'run');
  const [tenants, setTenants] = useState([]);
  const [flows, setFlows]   = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast]   = useState('');

  // tasarım modu
  const [designTenant, setDesignTenant] = useState(isSuperAdmin ? '' : String(user?.tenantId || ''));
  const [flow, setFlow]   = useState(() => emptyFlow(isSuperAdmin ? '' : user?.tenantId));
  const [selected, setSelected] = useState(null);
  const [dragId, setDragId]   = useState(null);
  const dragOffset = useRef({ x: 0, y: 0 });
  const [linkFrom, setLinkFrom] = useState(null);
  const canvasRef = useRef(null);

  // çalıştır modu
  const [runTenant, setRunTenant]       = useState(isSuperAdmin ? '' : String(user?.tenantId || ''));
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [statuses, setStatuses]         = useState({});
  const [nodeErrors, setNodeErrors]     = useState({}); // nodeId → hata mesajı
  const [stepDurations, setStepDurations] = useState({});
  const [running, setRunning]           = useState(false);
  const [logs, setLogs]                 = useState([]);
  const [runHistory, setRunHistory]     = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [showTestPanel, setShowTestPanel] = useState(false);
  const abortRef = useRef(null); // SSE iptal için AbortController

  // panel toggle
  const [leftCollapsed, setLeftCollapsed]   = useState(false);
  const [rightCollapsed, setRightCollapsed] = useState(false);

  const editor     = mode === 'design';
  const runFlow_   = flows.find((f) => f.id === selectedJobId) || null;
  const activeFlow = editor ? flow : runFlow_;

  const showToast = (m) => { setToast(m); setTimeout(() => setToast(''), 2600); };
  const log = useCallback((msg, level = 'info') =>
    setLogs((l) => [...l, { t: new Date(), msg, level }]), []);

  useEffect(() => {
    if (isSuperAdmin) getTenants().then(setTenants).catch(() => {});
  }, [isSuperAdmin]);

  const loadFlows = useCallback(async (tid) => {
    if (isSuperAdmin && !tid) { setFlows([]); return; }
    setLoading(true);
    try {
      const data   = await getEtlFlows(isSuperAdmin ? tid : null);
      const parsed = data.map((f) => {
        let def = { nodes: [], edges: [] };
        try { def = JSON.parse(f.flowDefinition || '{}'); } catch { }
        return { id: f.id, tenantId: f.tenantId, name: f.name, nodes: def.nodes || [], edges: def.edges || [] };
      });
      setFlows(parsed);
    } catch { showToast('Akışlar yüklenemedi.'); }
    finally { setLoading(false); }
  }, [isSuperAdmin]);

  useEffect(() => {
    loadFlows(editor ? designTenant : runTenant);
  }, [designTenant, runTenant, mode, loadFlows]);

  const topoOrder = useCallback((fl) => {
    const { nodes, edges } = fl;
    const incoming = {};
    nodes.forEach((n) => (incoming[n.id] = 0));
    edges.forEach((e) => (incoming[e.to] = (incoming[e.to] || 0) + 1));
    const queue  = nodes.filter((n) => incoming[n.id] === 0).map((n) => n.id);
    const order  = [], seen = new Set();
    while (queue.length) {
      const id = queue.shift();
      if (seen.has(id)) continue;
      seen.add(id); order.push(id);
      edges.filter((e) => e.from === id).forEach((e) => {
        incoming[e.to] -= 1;
        if (incoming[e.to] === 0) queue.push(e.to);
      });
    }
    nodes.forEach((n) => { if (!seen.has(n.id)) order.push(n.id); });
    return order;
  }, []);

  const fileNodes  = (fl) => (fl?.nodes || []).filter((n) => n.type === 'fileUpload');

  // ── drag ──────────────────────────────────────────────────────────────────
  const onMouseDownNode = (e, node) => {
    if (!editor || running) return;
    e.stopPropagation();
    setSelected(node.id);
    const rect = canvasRef.current.getBoundingClientRect();
    dragOffset.current = { x: e.clientX - rect.left - node.x, y: e.clientY - rect.top - node.y };
    setDragId(node.id);
  };
  const onMouseMove = (e) => {
    if (!dragId || !editor) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = Math.max(0, e.clientX - rect.left - dragOffset.current.x);
    const y = Math.max(0, e.clientY - rect.top - dragOffset.current.y);
    setFlow((f) => ({ ...f, nodes: f.nodes.map((n) => (n.id === dragId ? { ...n, x, y } : n)) }));
  };
  const onMouseUp = () => setDragId(null);

  // ── node CRUD ─────────────────────────────────────────────────────────────
  const addNode = (type) => {
    if (isSuperAdmin && !designTenant) { showToast('Önce tenant seçin'); return; }
    const base = { id: uid(), type, name: type === 'fileUpload' ? 'Yeni Dosya Yükleme' : 'Yeni SQL Adımı', x: 80 + Math.random() * 120, y: 80 + Math.random() * 120 };
    const n = type === 'fileUpload'
      ? { ...base, table: 'TmpYeniTablo', expectedFile: 'dosya.csv', format: 'flat', headerRow: 1, fixedCols: 2, valueCol: 'Units', keyCol: 'ProductName', fixedColNames: '' }
      : { ...base, sql: '' };
    setFlow((f) => ({ ...f, nodes: [...f.nodes, n] }));
    setSelected(n.id);
  };
  const deleteNode = (id) => {
    setFlow((f) => ({ ...f, nodes: f.nodes.filter((n) => n.id !== id), edges: f.edges.filter((e) => e.from !== id && e.to !== id) }));
    setSelected(null);
  };
  const updateNode = (id, patch) =>
    setFlow((f) => ({ ...f, nodes: f.nodes.map((n) => (n.id === id ? { ...n, ...patch } : n)) }));

  const startLink    = (e, id) => { e.stopPropagation(); if (editor && !running) setLinkFrom(id); };
  const completeLink = (e, id) => {
    e.stopPropagation();
    if (linkFrom && linkFrom !== id) {
      const exists = flow.edges.some((ed) => ed.from === linkFrom && ed.to === id);
      if (!exists) setFlow((f) => ({ ...f, edges: [...f.edges, { id: uid(), from: linkFrom, to: id }] }));
    }
    setLinkFrom(null);
  };
  const deleteEdge = (id) => setFlow((f) => ({ ...f, edges: f.edges.filter((e) => e.id !== id) }));
  const nodeById   = (id) => activeFlow?.nodes.find((n) => n.id === id);

  // ── kaydet ────────────────────────────────────────────────────────────────
  const saveFlow = async () => {
    const tid = isSuperAdmin ? designTenant : user?.tenantId;
    if (!tid) { showToast('Tenant seçin'); return; }
    if (!flow.name.trim()) { showToast('ETL adı girin'); return; }
    if (flow.nodes.length === 0) { showToast('En az bir node ekleyin'); return; }
    try {
      const def = { nodes: flow.nodes, edges: flow.edges };
      if (flow.id) {
        await updateEtlFlow(flow.id, Number(tid), flow.name.trim(), def);
        showToast('✅ ETL güncellendi');
      } else {
        const res = await createEtlFlow(Number(tid), flow.name.trim(), def);
        setFlow((f) => ({ ...f, id: res.id }));
        showToast('✅ ETL kaydedildi');
      }
      loadFlows(tid);
    } catch { showToast('Kaydetme başarısız.'); }
  };

  const newFlow = () => {
    const tid = isSuperAdmin ? designTenant : user?.tenantId;
    setFlow(emptyFlow(tid)); setSelected(null); setShowTestPanel(false);
    setStatuses({}); setStepDurations({}); setLogs([]);
  };
  const loadFlowForEdit = (f) => {
    if (isSuperAdmin) setDesignTenant(String(f.tenantId));
    setFlow(JSON.parse(JSON.stringify(f)));
    setSelected(null); setShowTestPanel(false);
    setStatuses({}); setStepDurations({}); setLogs([]);
  };
  const removeFlow = async (id) => {
    if (!window.confirm('Bu ETL akışı silinsin mi?')) return;
    try {
      await (await import('../services/etlService')).deleteEtlFlow(id);
      showToast('ETL silindi.');
      if (flow.id === id) newFlow();
      loadFlows(isSuperAdmin ? designTenant : user?.tenantId);
    } catch { showToast('Silme başarısız.'); }
  };

  const loadHistory = async (flowId) => {
    try {
      const data = await getEtlRunHistory(flowId);
      setRunHistory(data.map((r) => ({
        id: r.id, user: r.runBy,
        startedAt:  new Date(r.startedAt),
        duration:   r.durationSeconds,
        status:     r.status,
        error:      r.errorMessage,
      })));
    } catch { }
  };

  useEffect(() => {
    setStatuses({}); setStepDurations({}); setLogs([]); setUploadedFiles([]);
    if (!editor && selectedJobId) loadHistory(selectedJobId);
    // eslint-disable-next-line
  }, [selectedJobId, mode]);

  // ── SSE çalıştırma ────────────────────────────────────────────────────────
  // Her iki mod için ortak SSE runner.
  // targetFlow:  çalıştırılacak flow nesnesi
  // filesToSend: File[] listesi
  // onlyNodeId:  doluysa akışın tamamı değil yalnızca o node çalıştırılır
  const runWithSSE = useCallback(async (targetFlow, filesToSend, onlyNodeId = null) => {
    setRunning(true);
    setLogs([]);
    setStepDurations({});
    setNodeErrors(onlyNodeId ? (e) => ({ ...e, [onlyNodeId]: null }) : {});

    if (onlyNodeId) {
      // Tek adım: yalnızca hedef node'un durumunu sıfırla, diğerleri korunur
      setStatuses((s) => ({ ...s, [onlyNodeId]: 'idle' }));
      const only = targetFlow.nodes.find((n) => n.id === onlyNodeId);
      log(`▶ "${only?.name ?? onlyNodeId}" adımı çalıştırılıyor…`);
    } else {
      // Tüm node'ları idle'a al
      const idleInit = {};
      targetFlow.nodes.forEach((n) => (idleInit[n.id] = 'idle'));
      setStatuses(idleInit);
      log(`▶ "${targetFlow.name}" başlatılıyor…`);
    }

    // SSE iptal kontrolü
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    try {
      await runEtlFlowSSE({
        flowId:  targetFlow.id,
        files:   filesToSend,
        signal:  ctrl.signal,
        nodeId:  onlyNodeId,

        onEvent: (evt) => {
          switch (evt.type) {

            case 'step_start':
              // Node sarıya al, önceki hatasını temizle
              setStatuses((s) => ({ ...s, [evt.nodeId]: 'running' }));
              setNodeErrors((e) => ({ ...e, [evt.nodeId]: null }));
              log(`⏳ ${evt.stepName} başladı…`);
              break;

            case 'step_progress':
              log(`… ${evt.stepName}: ${Number(evt.rowsInserted).toLocaleString('tr-TR')} satır yüklendi`);
              break;

            case 'heartbeat':
              // Bağlantıyı canlı tutar; kullanıcıya gösterilmez.
              break;

            case 'step_done':
              if (evt.status === 'Success') {
                setStatuses((s) => ({ ...s, [evt.nodeId]: 'success' }));
                const dur = evt.durationMs != null ? ` (${(evt.durationMs / 1000).toFixed(1)} sn)` : '';
                setStepDurations((d) => ({ ...d, [evt.nodeId]: `${(evt.durationMs / 1000).toFixed(1)} sn` }));
                log(`✅ ${evt.stepName} tamamlandı${dur}.`, 'success');
              } else {
                setStatuses((s) => ({ ...s, [evt.nodeId]: 'failed' }));
                setNodeErrors((e) => ({ ...e, [evt.nodeId]: evt.errorMessage || 'Bilinmeyen hata' }));
                log(`❌ ${evt.stepName} — ${evt.errorMessage}`, 'error');
              }
              break;

            case 'run_done':
              if (onlyNodeId) {
                if (evt.status === 'Success') log('■ Adım tamamlandı.', 'success');
                else log(`■ Adım durdu. ${evt.errorMessage ?? ''}`, 'error');
                // Tek adım denemesi geçmişe yazılmaz — yenileme yok
              } else {
                if (evt.status === 'Success')
                  log(`■ Tüm adımlar tamamlandı. Toplam süre: ${evt.totalDurationSeconds} sn.`, 'success');
                else
                  log(`■ İşlem durdu. ${evt.errorMessage ?? ''}`, 'error');
                // Geçmişi yenile
                if (targetFlow.id) loadHistory(targetFlow.id);
              }
              break;

            default:
              break;
          }
        },
      });
    } catch (err) {
      if (err.name === 'AbortError') {
        log('⛔ Çalıştırma iptal edildi.', 'error');
      } else {
        log(`❌ Bağlantı hatası: ${err.message}`, 'error');
      }
    } finally {
      setRunning(false);
      abortRef.current = null;
    }
  }, [log]);

  // Çalıştır modu
  const runRealJob = () => {
    if (!runFlow_) return;
    const missing = fileNodes(runFlow_).filter((n) => !resolveFile(uploadedFiles, n.expectedFile));
    if (missing.length > 0) {
      log(`❌ Eksik dosya(lar): ${missing.map((m) => m.expectedFile).join(', ')}.`, 'error');
      return;
    }
    const filesToSend = fileNodes(runFlow_)
      .map((n) => resolveFile(uploadedFiles, n.expectedFile))
      .filter(Boolean);
    runWithSSE(runFlow_, filesToSend);
  };

  // Test modu (tasarım ekranından)
  const runTest = () => {
    if (!flow.id) { showToast('Önce kaydedin, sonra test edin'); return; }
    const missing = fileNodes(flow).filter((n) => !resolveFile(uploadedFiles, n.expectedFile));
    if (missing.length > 0) {
      log(`❌ Eksik dosya(lar): ${missing.map((m) => m.expectedFile).join(', ')}.`, 'error');
      return;
    }
    const filesToSend = fileNodes(flow)
      .map((n) => resolveFile(uploadedFiles, n.expectedFile))
      .filter(Boolean);
    runWithSSE(flow, filesToSend);
  };

  // Tasarımdaki flow, kaydedilmiş sürümünden farklı mı? (sunucu kayıtlı tanımı
  // çalıştırdığı için, kaydedilmemiş değişiklikler çalıştırmaya yansımaz.)
  const isDirty = () => {
    if (!flow.id) return true;
    const saved = flows.find((f) => f.id === flow.id);
    if (!saved) return true;
    const norm = (f) => JSON.stringify({ nodes: f.nodes, edges: f.edges });
    return norm(saved) !== norm(flow) || saved.name !== flow.name;
  };

  // Tasarım modunda tek bir node'u çalıştır (sunucu, kayıtlı tanımdaki bu node'u
  // izole çalıştırır; öncül adımlar çalışmaz, geçmişe yazılmaz).
  const runSingleNode = (node) => {
    if (!node) return;
    if (!flow.id) { showToast('Önce kaydedin, sonra çalıştırın'); return; }
    if (isDirty()) { showToast('Kaydedilmemiş değişiklik var — önce kaydedin'); return; }

    let filesToSend = [];
    if (node.type === 'fileUpload') {
      const file = resolveFile(uploadedFiles, node.expectedFile);
      if (!file) {
        showToast(`Bu adım için dosya seçin: ${node.expectedFile}`);
        log(`❌ Eksik dosya: ${node.expectedFile}.`, 'error');
        return;
      }
      filesToSend = [file];
    }
    runWithSSE(flow, filesToSend, node.id);
  };

  const cancelRun = () => {
    if (abortRef.current) abortRef.current.abort();
  };

  const resetRun = () => {
    const init = {};
    (activeFlow?.nodes || []).forEach((n) => (init[n.id] = 'idle'));
    setStatuses(init); setStepDurations({}); setLogs([]);
  };

  const sel        = selected ? activeFlow?.nodes.find((n) => n.id === selected) : null;
  const canTest    = editor && (isSuperAdmin ? !!designTenant : true) && flow.nodes.length > 0;

  const edgePath = (e) => {
    const a = nodeById(e.from), b = nodeById(e.to);
    if (!a || !b) return null;
    const x1 = a.x + NODE_W / 2, y1 = a.y + NODE_H;
    const x2 = b.x + NODE_W / 2, y2 = b.y;
    const my = (y1 + y2) / 2;
    return { d: `M ${x1} ${y1} C ${x1} ${my}, ${x2} ${my}, ${x2} ${y2}`, mx: (x1 + x2) / 2, midy: my };
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={S.wrap} onMouseMove={onMouseMove} onMouseUp={onMouseUp}>
      <style>{pulseKeyframes}</style>
      {toast && <div style={S.toast}>{toast}</div>}

      {/* Mod geçişi */}
      <div style={S.modeBar}>
        <div style={S.modeToggle}>
          {isSuperAdmin && (
            <button onClick={() => setMode('design')} style={{ ...S.modeBtn, ...(editor ? S.modeBtnActive : {}) }}>
              🎨 Tasarım
            </button>
          )}
          <button onClick={() => setMode('run')} style={{ ...S.modeBtn, ...(!editor ? S.modeBtnActive : {}) }}>
            ▶ Çalıştır
          </button>
        </div>
      </div>

      <div style={S.body}>

        {/* ── SOL PANEL ── */}
        {!leftCollapsed && (
          <div style={S.left}>
            {editor ? (
              <>
                {isSuperAdmin && (
                  <>
                    <div style={S.panelTitle}>1 · Tenant</div>
                    <select style={S.sel} value={designTenant}
                      onChange={(e) => { setDesignTenant(e.target.value); setFlow(emptyFlow(e.target.value)); setSelected(null); }}>
                      <option value="">Tenant seçin…</option>
                      {tenants.filter((t) => t.isActive).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                  </>
                )}
                {(!isSuperAdmin || designTenant) && (
                  <>
                    <div style={S.panelTitle}>{isSuperAdmin ? '2 · ETL Adı' : 'ETL Adı'}</div>
                    <input style={S.inp} placeholder="örn. Stemline Haftalık Yükleme" value={flow.name}
                      onChange={(e) => setFlow((f) => ({ ...f, name: e.target.value }))} />

                    <div style={S.panelTitle}>Araç Kutusu</div>
                    <button style={{ ...S.toolBtn, borderColor: NODE_TYPES.fileUpload.color }} onClick={() => addNode('fileUpload')}>
                      <span>{NODE_TYPES.fileUpload.icon}</span> Dosya Yükleme
                    </button>
                    <button style={{ ...S.toolBtn, borderColor: NODE_TYPES.sqlExecute.color }} onClick={() => addNode('sqlExecute')}>
                      <span>{NODE_TYPES.sqlExecute.icon}</span> SQL Adımı
                    </button>

                    <div style={{ display: 'flex', gap: 6 }}>
                      <button style={S.saveBtn} onClick={saveFlow}>💾 {flow.id ? 'Güncelle' : 'Kaydet'}</button>
                      <button style={S.newBtn}  onClick={newFlow}>+ Yeni</button>
                    </div>

                    <div style={S.panelTitle}>Kayıtlı ETL&apos;ler</div>
                    <div style={S.savedList}>
                      {loading && <div style={S.logEmpty}>Yükleniyor…</div>}
                      {!loading && flows.length === 0 && <div style={S.logEmpty}>Kayıt yok.</div>}
                      {flows.map((f) => (
                        <div key={f.id} style={{ ...S.savedRow, ...(flow.id === f.id ? S.savedRowActive : {}) }}>
                          <span onClick={() => loadFlowForEdit(f)} style={{ flex: 1, cursor: 'pointer', fontSize: 12.5 }}>{f.name}</span>
                          <span onClick={() => removeFlow(f.id)} style={S.savedDel}>✕</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </>
            ) : (
              <>
                {isSuperAdmin && (
                  <>
                    <div style={S.panelTitle}>Tenant</div>
                    <select style={S.sel} value={runTenant}
                      onChange={(e) => { setRunTenant(e.target.value); setSelectedJobId(null); }}>
                      <option value="">Tenant seçin…</option>
                      {tenants.filter((t) => t.isActive).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                  </>
                )}
                {(!isSuperAdmin || runTenant) && (
                  <>
                    <div style={S.panelTitle}>ETL Job&apos;ları</div>
                    <div style={S.savedList}>
                      {loading && <div style={S.logEmpty}>Yükleniyor…</div>}
                      {!loading && flows.length === 0 && <div style={S.logEmpty}>Tanımlı ETL yok.</div>}
                      {flows.map((f) => (
                        <div key={f.id} onClick={() => setSelectedJobId(f.id)}
                          style={{ ...S.jobRow, ...(selectedJobId === f.id ? S.jobRowActive : {}) }}>
                          <div style={{ fontSize: 13, fontWeight: 600 }}>{f.name}</div>
                          <div style={{ fontSize: 11, color: '#94a3b8' }}>{f.nodes.length} adım · {fileNodes(f).length} dosya</div>
                        </div>
                      ))}
                    </div>

                    {selectedJobId && (
                      <>
                        <div style={S.panelTitle}>Dosyalar</div>
                        <label style={S.fileLabel}>
                          📎 Dosya(lar) Seç
                          <input type="file" accept=".txt,.csv,.xlsx,.xls" multiple style={{ display: 'none' }}
                            onChange={(e) => setUploadedFiles(Array.from(e.target.files))} />
                        </label>

                        {/* Eşleştirme tablosu — wildcard destekli, gerçek adı gösterir */}
                        <div style={S.matchBox}>
                          {fileNodes(runFlow_).map((n) => {
                            const matched = resolveFile(uploadedFiles, n.expectedFile);
                            return (
                              <div key={n.id} style={S.matchRow}>
                                <span style={{ fontSize: 11, fontFamily: 'monospace', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {n.expectedFile}
                                </span>
                                <div style={{ textAlign: 'right', minWidth: 60 }}>
                                  {matched ? (
                                    <>
                                      <span style={{ fontSize: 11, fontWeight: 700, color: '#16a34a' }}>✓</span>
                                      {/* Wildcard eşleşmesinde gerçek adı göster */}
                                      {matched.name.toLowerCase() !== (n.expectedFile || '').toLowerCase() && (
                                        <div style={{ fontSize: 10, color: '#6b7280', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                          {matched.name}
                                        </div>
                                      )}
                                    </>
                                  ) : (
                                    <span style={{ fontSize: 11, fontWeight: 700, color: '#dc2626' }}>eksik</span>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        <button style={{ ...S.runBtn, opacity: running ? 0.6 : 1 }} disabled={running} onClick={runRealJob}>
                          {running ? '⏳ Çalışıyor…' : '▶ Çalıştır'}
                        </button>
                        {running && (
                          <button style={S.cancelBtn} onClick={cancelRun}>⛔ İptal</button>
                        )}
                        {!running && (
                          <button style={S.resetBtn} onClick={resetRun}>↺ Sıfırla</button>
                        )}
                      </>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        )}

        {/* ── CANVAS ── */}
        <div style={S.canvasWrap}>
          <button onClick={() => setLeftCollapsed((v) => !v)} style={{ ...S.panelToggle, left: 8 }}
            title={leftCollapsed ? 'Sol paneli aç' : 'Sol paneli kapat'}>
            {leftCollapsed ? '▶' : '◀'}
          </button>
          <button onClick={() => setRightCollapsed((v) => !v)} style={{ ...S.panelToggle, right: 8 }}
            title={rightCollapsed ? 'Sağ paneli aç' : 'Sağ paneli kapat'}>
            {rightCollapsed ? '◀' : '▶'}
          </button>

          {/* Test çalıştır (tasarım modu) */}
          {canTest && (
            <div style={S.testBar}>
              <button style={{ ...S.testBtn, opacity: running ? 0.5 : 1 }} disabled={running}
                onClick={() => setShowTestPanel((v) => !v)}>
                🧪 Test Çalıştır
              </button>
              {showTestPanel && (
                <div style={S.testPanel}>
                  {!flow.id && (
                    <div style={{ fontSize: 11.5, color: '#dc2626' }}>Önce kaydedin, sonra test edebilirsiniz.</div>
                  )}
                  {fileNodes(flow).length > 0 && (
                    <>
                      <label style={S.fileLabelSm}>
                        📎 Dosya Seç (çoklu)
                        <input type="file" accept=".txt,.csv,.xlsx,.xls" multiple style={{ display: 'none' }}
                          onChange={(e) => setUploadedFiles(Array.from(e.target.files))} />
                      </label>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {fileNodes(flow).map((n) => {
                          const matched = resolveFile(uploadedFiles, n.expectedFile);
                          return (
                            <div key={n.id} style={{ display: 'flex', gap: 6, fontSize: 11, alignItems: 'flex-start' }}>
                              <span style={{ fontFamily: 'monospace', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {n.expectedFile}
                              </span>
                              <div style={{ minWidth: 50, textAlign: 'right' }}>
                                {matched ? (
                                  <>
                                    <span style={{ fontWeight: 700, color: '#16a34a' }}>✓</span>
                                    {matched.name.toLowerCase() !== (n.expectedFile || '').toLowerCase() && (
                                      <div style={{ fontSize: 10, color: '#6b7280' }}>{matched.name}</div>
                                    )}
                                  </>
                                ) : (
                                  <span style={{ fontWeight: 700, color: '#dc2626' }}>eksik</span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button style={{ ...S.testRunBtn, opacity: running || !flow.id ? 0.5 : 1 }}
                      disabled={running || !flow.id} onClick={runTest}>
                      {running ? '⏳ Çalışıyor…' : '▶ Çalıştır'}
                    </button>
                    {running
                      ? <button style={S.cancelBtnSm} onClick={cancelRun}>⛔</button>
                      : <button style={S.resetBtnSm}  onClick={resetRun}>↺</button>
                    }
                  </div>
                </div>
              )}
            </div>
          )}

          {!activeFlow || (editor && isSuperAdmin && !designTenant) ? (
            <div style={S.placeholder}>
              {editor ? 'Başlamak için soldan bir tenant seçin.' : 'Çalıştırmak için soldan bir ETL job seçin.'}
            </div>
          ) : (
            <div ref={canvasRef} style={S.canvas}
              onClick={() => { setSelected(null); setLinkFrom(null); }}>
              {!editor && (
                <div style={S.canvasBanner}>
                  {activeFlow.name} <span style={{ color: '#94a3b8', fontWeight: 400 }}>· salt okunur</span>
                </div>
              )}

              <svg style={S.svgLayer}>
                <defs>
                  <marker id="arrow" markerWidth="10" markerHeight="10" refX="8" refY="3"
                    orient="auto" markerUnits="strokeWidth">
                    <path d="M0,0 L0,6 L9,3 z" fill="#64748b" />
                  </marker>
                </defs>
                {activeFlow.edges.map((e) => {
                  const p = edgePath(e); if (!p) return null;
                  return (
                    <g key={e.id}>
                      <path d={p.d} style={{ stroke: '#64748b', strokeWidth: 2, fill: 'none' }} markerEnd="url(#arrow)" />
                      {editor && !running && (
                        <g onClick={(ev) => { ev.stopPropagation(); deleteEdge(e.id); }} style={{ cursor: 'pointer' }}>
                          <circle cx={p.mx} cy={p.midy} r="9" fill="#fff" stroke="#dc2626" />
                          <text x={p.mx} y={p.midy + 4} textAnchor="middle" fontSize="11" fill="#dc2626">✕</text>
                        </g>
                      )}
                    </g>
                  );
                })}
              </svg>

              {activeFlow.nodes.map((n) => {
                const t          = NODE_TYPES[n.type];
                const nodeStatus = statuses[n.id] || 'idle';
                const st         = STATUS_STYLE[nodeStatus];
                const isSel      = selected === n.id;
                const isLinkSrc  = linkFrom === n.id;
                const showStatus = nodeStatus !== 'idle';
                const dur        = stepDurations[n.id];

                const nodeBg =
                  nodeStatus === 'success' ? '#f0fdf4' :
                  nodeStatus === 'failed'  ? '#fef2f2' :
                  nodeStatus === 'running' ? '#fffbeb' : '#fff';

                return (
                  <div key={n.id}
                    style={{
                      ...S.node,
                      left: n.x, top: n.y, width: NODE_W, minHeight: NODE_H,
                      background:  nodeBg,
                      borderColor: showStatus ? st.ring : (isSel ? t.color : '#e2e8f0'),
                      boxShadow:   nodeStatus === 'running' ? undefined
                        : isSel ? `0 0 0 3px ${t.color}33` : '0 2px 8px rgba(0,0,0,.08)',
                      animation:   nodeStatus === 'running' ? 'etlPulse 1s infinite' : 'none',
                      cursor:      editor && !running ? 'grab' : 'default',
                    }}
                    onMouseDown={(e) => onMouseDownNode(e, n)}
                    onClick={(e) => { e.stopPropagation(); if (editor && !running) setSelected(n.id); }}>

                    {/* Giriş portu */}
                    <div style={{ ...S.port, top: -7, left: NODE_W / 2 - 7, borderColor: linkFrom ? '#16a34a' : '#94a3b8' }}
                      onClick={(e) => completeLink(e, n.id)} title="Giriş" />

                    <div style={S.nodeHead}>
                      <span style={{ fontSize: 15 }}>{t.icon}</span>
                      <span style={{ ...S.nodeType, color: t.color }}>{t.label}</span>
                      <span style={{ marginLeft: 'auto', fontSize: 14, lineHeight: 1 }}>
                        {nodeStatus === 'running' && '⏳'}
                        {nodeStatus === 'success' && '✅'}
                        {nodeStatus === 'failed'  && '❌'}
                      </span>
                    </div>

                    <div style={S.nodeName}>{n.name}</div>

                    {n.type === 'fileUpload' && (
                      <>
                        <div style={S.nodeSub}>📁 {n.expectedFile}</div>
                        <div style={S.nodeSub}>→ {n.table} {n.format === 'unpivot' ? '· ⇄ unpivot' : n.format === 'multiUnpivot' ? '· ⇄ multi' : '· 1:1'}</div>
                      </>
                    )}

                    {showStatus && (
                      <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 4 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: st.badge }}>{st.text}</span>
                        {dur && nodeStatus === 'success' && (
                          <span style={{ fontSize: 10.5, color: '#16a34a', fontWeight: 600, background: '#dcfce7', padding: '1px 7px', borderRadius: 10, whiteSpace: 'nowrap' }}>
                            ⏱ {dur}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Çıkış portu */}
                    <div style={{ ...S.port, bottom: -7, left: NODE_W / 2 - 7, borderColor: isLinkSrc ? '#16a34a' : '#94a3b8', background: isLinkSrc ? '#16a34a' : '#fff' }}
                      onClick={(e) => startLink(e, n.id)} title="Çıkış" />
                  </div>
                );
              })}

              {linkFrom && (
                <div style={S.linkHint}>
                  Hedef node&apos;un üst noktasına tıkla — iptal için boşluğa tıkla
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── SAĞ PANEL ── */}
        {!rightCollapsed && (
          <div style={S.right}>
            {editor && sel ? (
              <>
                <div style={S.panelTitle}>Node Ayarları</div>

                {/* Tek adım çalıştırma — tüm akışı baştan çalıştırmadan bu node'u dener */}
                <button
                  style={{ ...S.runNodeBtn, opacity: running ? 0.5 : 1 }}
                  disabled={running}
                  onClick={() => runSingleNode(sel)}>
                  ▶ Bu Adımı Çalıştır
                </button>
                {isDirty() && (
                  <div style={S.dirtyHint}>Kaydedilmemiş değişiklik var — çalıştırmadan önce kaydedin.</div>
                )}
                {nodeErrors[sel.id] && (
                  <div style={S.nodeErrBox}>
                    <div style={{ fontWeight: 700, marginBottom: 2 }}>❌ Hata</div>
                    {nodeErrors[sel.id]}
                  </div>
                )}
                {sel.type === 'fileUpload' && (
                  <div style={{ marginBottom: 6 }}>
                    <label style={S.fileLabelSm}>
                      📎 Dosya Seç
                      <input type="file" accept=".txt,.csv,.xlsx,.xls" multiple style={{ display: 'none' }}
                        onChange={(e) => setUploadedFiles(Array.from(e.target.files))} />
                    </label>
                    <div style={{ fontSize: 11, marginTop: 3 }}>
                      {resolveFile(uploadedFiles, sel.expectedFile) ? (
                        <span style={{ color: '#16a34a', fontWeight: 700 }}>
                          ✓ {resolveFile(uploadedFiles, sel.expectedFile).name}
                        </span>
                      ) : (
                        <span style={{ color: '#b45309' }}>Bu adım için dosya seçilmedi.</span>
                      )}
                    </div>
                  </div>
                )}

                <label style={S.lbl}>Adım Adı</label>
                <input style={S.inp} value={sel.name}
                  onChange={(e) => updateNode(sel.id, { name: e.target.value })} />

                {sel.type === 'fileUpload' ? (
                  <>
                    <label style={S.lbl}>Beklenen Dosya Adı</label>
                    <input style={S.inp} value={sel.expectedFile || ''}
                      placeholder="örn: Weekly_M_Unit*.xlsx"
                      onChange={(e) => updateNode(sel.id, { expectedFile: e.target.value })} />
                    {/* Wildcard hint */}
                    {(sel.expectedFile?.includes('*') || sel.expectedFile?.includes('?')) && (
                      <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2, padding: '3px 6px', background: '#f9fafb', borderRadius: 4, border: '1px solid #e5e7eb' }}>
                        <b>*</b> → herhangi karakter dizisi &nbsp;|&nbsp; <b>?</b> → tek karakter
                      </div>
                    )}
                    <div style={S.hintSm}>Wildcard kullanabilirsiniz: Weekly_M_Unit*.xlsx</div>

                    <label style={S.lbl}>Hedef Tmp Tablo</label>
                    <input style={S.inp} value={sel.table || ''}
                      onChange={(e) => updateNode(sel.id, { table: e.target.value })} />

                    <label style={S.lbl}>Format</label>
                    <select style={S.inp} value={sel.format}
                      onChange={(e) => updateNode(sel.id, { format: e.target.value })}>
                      <option value="flat">Düz (1:1 kolon eşleme)</option>
                      <option value="unpivot">Geniş→Uzun (Unpivot)</option>
                      <option value="multiUnpivot">Çok-Measure (Geniş→Uzun, 6'lı blok)</option>
                    </select>

                    {sel.format === 'unpivot' && (
                      <div style={S.unpivotBox}>
                        <div style={S.unpivotTitle}>Unpivot Ayarları</div>
                        <label style={S.lbl}>Ürün Başlık Satırı</label>
                        <input type="number" min="1" style={S.inp} value={sel.headerRow || 1}
                          onChange={(e) => updateNode(sel.id, { headerRow: parseInt(e.target.value) || 1 })} />
                        <label style={S.lbl}>Sabit Kolon Başlık Satırı</label>
                        <input type="number" min="1" style={S.inp} value={sel.fixedHeaderRow || 4}
                          onChange={(e) => updateNode(sel.id, { fixedHeaderRow: parseInt(e.target.value) || 1 })} />
                        <div style={S.hintSm}>TERRITORIES, Month gibi sabit kolon adlarının bulunduğu satır.</div>
                        <label style={S.lbl}>Veri Başlangıç Satırı</label>
                        <input type="number" min="1" style={S.inp} value={sel.dataStartRow || 5}
                          onChange={(e) => updateNode(sel.id, { dataStartRow: parseInt(e.target.value) || 1 })} />
                        <div style={S.hintSm}>Gerçek verinin başladığı satır.</div>
                        <label style={S.lbl}>Sabit Kolon Sayısı</label>
                        <input type="number" min="0" style={S.inp} value={sel.fixedCols ?? 2}
                          onChange={(e) => updateNode(sel.id, { fixedCols: parseInt(e.target.value) || 0 })} />
                        <label style={S.lbl}>Sabit Kolon Adları (virgülle)</label>
                        <input style={S.inp} value={sel.fixedColNames || ''} placeholder="TERRITORIES,Month"
                          onChange={(e) => updateNode(sel.id, { fixedColNames: e.target.value })} />
                        <div style={S.hintSm}>Boş bırakılırsa Sabit Kolon Başlık Satırından otomatik okunur.</div>
                        <label style={S.lbl}>Anahtar Kolon (ürün)</label>
                        <input style={S.inp} value={sel.keyCol || 'ProductName'}
                          onChange={(e) => updateNode(sel.id, { keyCol: e.target.value })} />
                        <label style={S.lbl}>Değer Kolon</label>
                        <input style={S.inp} value={sel.valueCol || 'Units'}
                          onChange={(e) => updateNode(sel.id, { valueCol: e.target.value })} />
                        <div style={S.hintSm}>Ürün kolonları satıra açılır; şema sabit kalır.</div>
                      </div>
                    )}

                    {sel.format === 'multiUnpivot' && (
                      <div style={S.unpivotBox}>
                        <div style={S.unpivotTitle}>Çok-Measure Unpivot Ayarları</div>
                        <label style={S.lbl}>Başlık Satırı</label>
                        <input type="number" min="1" style={S.inp} value={sel.headerRow || 1}
                          onChange={(e) => updateNode(sel.id, { headerRow: parseInt(e.target.value) || 1 })} />
                        <div style={S.hintSm}>Ay + measure adının bulunduğu tek başlık satırı.</div>
                        <label style={S.lbl}>Veri Başlangıç Satırı</label>
                        <input type="number" min="1" style={S.inp} value={sel.dataStartRow || 2}
                          onChange={(e) => updateNode(sel.id, { dataStartRow: parseInt(e.target.value) || 1 })} />
                        <label style={S.lbl}>Sabit Dimension Kolon Sayısı</label>
                        <input type="number" min="0" style={S.inp} value={sel.fixedCols ?? 15}
                          onChange={(e) => updateNode(sel.id, { fixedCols: parseInt(e.target.value) || 0 })} />
                        <label style={S.lbl}>Sabit Kolon Adları (virgülle)</label>
                        <input style={S.inp} value={sel.fixedColNames || ''} placeholder="ATC1,ATC2,...,LaunchYear"
                          onChange={(e) => updateNode(sel.id, { fixedColNames: e.target.value })} />
                        <div style={S.hintSm}>Boş bırakılırsa Başlık Satırından ilk N kolon okunur.</div>
                        <label style={S.lbl}>Measure Adları (sıralı, virgülle)</label>
                        <input style={S.inp} value={sel.measureNames || ''} placeholder="ExManfPrice,TL,USD,EUR,Units,StdUnit"
                          onChange={(e) => updateNode(sel.id, { measureNames: e.target.value })} />
                        <div style={S.hintSm}>Her ay bloğundaki measure sırası; blok boyutunu belirler.</div>
                        <label style={S.lbl}>Ay Kolon Adı</label>
                        <input style={S.inp} value={sel.monthCol || 'Month'}
                          onChange={(e) => updateNode(sel.id, { monthCol: e.target.value })} />
                        <label style={S.lbl}>Ay Başlık Formatı</label>
                        <input style={S.inp} value={sel.monthFormat || 'MMM yyyy'} placeholder="MMM yyyy"
                          onChange={(e) => updateNode(sel.id, { monthFormat: e.target.value })} />
                        <div style={S.hintSm}>Örn. "Jun 2020" → MMM yyyy; ayın ilk gününe çevrilir.</div>
                        <div style={S.hintSm}>Measure/sabit kolon ve ay adları hedef tablo kolonlarıyla birebir eşleşmeli.</div>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <label style={S.lbl}>SQL</label>
                    <textarea style={S.sqlArea} value={sel.sql} spellCheck={false}
                      onChange={(e) => updateNode(sel.id, { sql: e.target.value })}
                      placeholder="-- SQL adımı" />
                  </>
                )}
                <button style={S.delBtn} onClick={() => deleteNode(sel.id)}>🗑 Node&apos;u Sil</button>
              </>
            ) : (logs.length > 0 || running || (!editor && selectedJobId)) ? (
              <>
                <div style={S.panelTitle}>Çalıştırma Günlüğü</div>
                <div style={S.logBox}>
                  {logs.length === 0 && <div style={S.logEmpty}>Henüz çalıştırılmadı.</div>}
                  {logs.map((l, i) => (
                    <div key={i} style={{ ...S.logLine, color: l.level === 'error' ? '#fca5a5' : l.level === 'success' ? '#86efac' : '#cbd5e1' }}>
                      <span style={S.logTime}>{l.t.toLocaleTimeString('tr-TR')}</span> {l.msg}
                    </div>
                  ))}
                </div>

                <div style={{ ...S.panelTitle, marginTop: 14 }}>Geçmiş Çalıştırmalar</div>
                <div style={S.histBox}>
                  {runHistory.length === 0 && <div style={S.logEmpty}>Kayıt yok.</div>}
                  {runHistory.map((r) => (
                    <div key={r.id} style={S.histRow}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <b style={{ fontSize: 12 }}>{r.startedAt.toLocaleDateString('tr-TR')}</b>
                        <span style={{ fontSize: 11, fontWeight: 700, color: r.status === 'Success' ? '#16a34a' : r.status === 'Failed' ? '#dc2626' : '#f59e0b' }}>
                          {r.status === 'Success' ? '✅ Başarılı' : r.status === 'Failed' ? '❌ Hata' : '⏳ ' + r.status}
                        </span>
                      </div>
                      <div style={S.histMeta}>👤 {r.user} · ⏱ {r.duration ?? '-'} sn · {r.startedAt.toLocaleTimeString('tr-TR')}</div>
                      {r.error && <div style={S.histErr}>↳ {r.error}</div>}
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div style={S.logEmpty}>{editor ? 'Bir node seç, ya da 🧪 Test Çalıştır.' : 'Bir ETL job seç.'}</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Stiller ─────────────────────────────────────────────────────────────────
const S = {
  wrap:           { position: 'relative', height: 'calc(100vh - 180px)', minHeight: 560, display: 'flex', flexDirection: 'column', userSelect: 'none' },
  toast:          { position: 'absolute', top: 10, left: '50%', transform: 'translateX(-50%)', background: '#0f172a', color: '#fff', padding: '10px 18px', borderRadius: 8, fontSize: 13, fontWeight: 600, zIndex: 100, boxShadow: '0 8px 24px rgba(0,0,0,.2)' },
  modeBar:        { display: 'flex', justifyContent: 'flex-end', marginBottom: 12 },
  modeToggle:     { display: 'flex', background: '#1e293b', borderRadius: 8, padding: 3, gap: 3 },
  modeBtn:        { border: 'none', background: 'transparent', color: '#94a3b8', padding: '6px 14px', borderRadius: 6, fontSize: 12.5, cursor: 'pointer', fontWeight: 600 },
  modeBtnActive:  { background: '#7c3aed', color: '#fff' },
  body:           { flex: 1, display: 'flex', border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden', background: '#fff' },
  left:           { width: 250, background: '#fff', borderRight: '1px solid #e2e8f0', padding: 16, display: 'flex', flexDirection: 'column', gap: 9, flexShrink: 0, overflow: 'auto' },
  right:          { width: 330, background: '#fff', borderLeft: '1px solid #e2e8f0', padding: 16, display: 'flex', flexDirection: 'column', gap: 8, flexShrink: 0, overflow: 'auto' },
  panelTitle:     { fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.6, color: '#64748b', marginTop: 4 },
  sel:            { padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13 },
  toolBtn:        { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', background: '#f8fafc', border: '2px solid', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', color: '#1e293b' },
  saveBtn:        { flex: 1, padding: '9px', background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 8, fontSize: 12.5, fontWeight: 700, cursor: 'pointer' },
  newBtn:         { padding: '9px 12px', background: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 12.5, fontWeight: 600, cursor: 'pointer' },
  savedList:      { display: 'flex', flexDirection: 'column', gap: 5, maxHeight: 150, overflow: 'auto' },
  savedRow:       { display: 'flex', alignItems: 'center', gap: 6, padding: '7px 9px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6 },
  savedRowActive: { background: '#f5f3ff', borderColor: '#c4b5fd' },
  savedDel:       { color: '#dc2626', cursor: 'pointer', fontSize: 12, fontWeight: 700 },
  jobRow:         { padding: '10px 11px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, cursor: 'pointer' },
  jobRowActive:   { background: '#ecfdf5', borderColor: '#6ee7b7' },
  matchBox:       { display: 'flex', flexDirection: 'column', gap: 5, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 9 },
  matchRow:       { display: 'flex', alignItems: 'flex-start', gap: 8 },
  hintSm:         { fontSize: 11, color: '#94a3b8', lineHeight: 1.5 },
  unpivotBox:     { background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: 8, padding: 10, display: 'flex', flexDirection: 'column', gap: 4, marginTop: 4 },
  unpivotTitle:   { fontSize: 11, fontWeight: 800, color: '#7c3aed', textTransform: 'uppercase', letterSpacing: 0.4 },
  canvasWrap:     { flex: 1, position: 'relative', overflow: 'auto' },
  panelToggle:    { position: 'absolute', top: '50%', transform: 'translateY(-50%)', zIndex: 20, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6, width: 24, height: 48, cursor: 'pointer', fontSize: 11, color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,.1)' },
  testBar:        { position: 'absolute', top: 10, right: 40, zIndex: 25, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 },
  testBtn:        { background: '#f59e0b', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer', boxShadow: '0 2px 8px rgba(245,158,11,.3)' },
  testPanel:      { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: 12, width: 240, display: 'flex', flexDirection: 'column', gap: 8, boxShadow: '0 8px 24px rgba(0,0,0,.12)' },
  fileLabelSm:    { display: 'block', padding: '8px', border: '2px dashed #93c5fd', borderRadius: 6, fontSize: 12, fontWeight: 600, color: '#0ea5e9', cursor: 'pointer', textAlign: 'center', background: '#f0f9ff' },
  testRunBtn:     { flex: 1, padding: '9px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  resetBtnSm:     { padding: '9px 12px', background: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13, cursor: 'pointer' },
  cancelBtnSm:    { padding: '9px 12px', background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 8, fontSize: 13, cursor: 'pointer' },
  placeholder:    { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#94a3b8', fontSize: 14, fontStyle: 'italic' },
  canvas:         { position: 'relative', width: '100%', minWidth: 400, height: '100%', minHeight: 480, backgroundColor: '#f1f5f9', backgroundImage: 'radial-gradient(#cbd5e1 1px, transparent 1px)', backgroundSize: '20px 20px' },
  canvasBanner:   { position: 'absolute', top: 10, left: 40, background: '#fff', border: '1px solid #e2e8f0', padding: '5px 12px', borderRadius: 20, fontSize: 12.5, fontWeight: 700, zIndex: 15, boxShadow: '0 1px 4px rgba(0,0,0,.06)' },
  svgLayer:       { position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', overflow: 'visible' },
  node:           { position: 'absolute', borderRadius: 10, border: '2px solid', padding: '9px 12px', userSelect: 'none', display: 'flex', flexDirection: 'column', gap: 1, transition: 'border-color .2s, background .2s' },
  nodeHead:       { display: 'flex', alignItems: 'center', gap: 6 },
  nodeType:       { fontSize: 10.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.4 },
  nodeName:       { fontSize: 13, fontWeight: 700, color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  nodeSub:        { fontSize: 10.5, color: '#94a3b8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  port:           { position: 'absolute', width: 14, height: 14, borderRadius: '50%', background: '#fff', border: '2px solid', cursor: 'pointer', zIndex: 5 },
  linkHint:       { position: 'absolute', top: 8, left: '50%', transform: 'translateX(-50%)', background: '#16a34a', color: '#fff', padding: '5px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, zIndex: 20 },
  lbl:            { fontSize: 11.5, fontWeight: 700, color: '#475569', marginTop: 6 },
  inp:            { padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13 },
  sqlArea:        { padding: 10, border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 12, fontFamily: "'Consolas','Monaco',monospace", minHeight: 200, resize: 'vertical', lineHeight: 1.5, background: '#0f172a', color: '#e2e8f0' },
  delBtn:         { marginTop: 14, padding: '9px', background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 8, fontSize: 12.5, fontWeight: 700, cursor: 'pointer' },
  runNodeBtn:     { width: '100%', marginTop: 8, marginBottom: 4, padding: '10px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  dirtyHint:      { fontSize: 11, color: '#b45309', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '6px 8px', marginBottom: 4 },
  nodeErrBox:     { fontSize: 11.5, color: '#b91c1c', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '7px 9px', marginBottom: 6, wordBreak: 'break-word', whiteSpace: 'pre-wrap' },
  fileLabel:      { display: 'block', padding: '11px 12px', border: '2px dashed #93c5fd', borderRadius: 8, fontSize: 13, fontWeight: 600, color: '#0ea5e9', cursor: 'pointer', textAlign: 'center', background: '#f0f9ff' },
  runBtn:         { padding: '12px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer' },
  cancelBtn:      { padding: '8px', background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 8, fontSize: 12.5, fontWeight: 600, cursor: 'pointer' },
  resetBtn:       { padding: '8px', background: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 12.5, fontWeight: 600, cursor: 'pointer' },
  logBox:         { background: '#0f172a', borderRadius: 8, padding: 12, fontFamily: "'Consolas','Monaco',monospace", fontSize: 11.5, minHeight: 170, maxHeight: 250, overflow: 'auto', lineHeight: 1.6 },
  logLine:        { marginBottom: 2 },
  logTime:        { color: '#475569', marginRight: 6 },
  logEmpty:       { color: '#94a3b8', fontSize: 12, fontStyle: 'italic', padding: 8 },
  histBox:        { display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 200, overflow: 'auto' },
  histRow:        { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 10 },
  histMeta:       { fontSize: 11, color: '#64748b', marginTop: 3 },
  histErr:        { fontSize: 11, color: '#dc2626', marginTop: 3, fontFamily: 'monospace' },
};

export default EtlPage;